import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { acceptInvite } from '../services/api';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import toast from 'react-hot-toast';
import { ShieldCheck, Check, AlertCircle } from 'lucide-react';

const acceptInviteSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const AcceptInvitePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || searchParams.get('inviteToken');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(acceptInviteSchema),
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4">
        <div className="w-full max-w-md p-8 glass rounded-2xl z-10 text-center space-y-6">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto shadow-sm">
            <AlertCircle size={32} />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-slate-900">Invitation Error</h2>
            <p className="text-slate-500">Invalid or missing invite token.</p>
          </div>
          <Button className="w-full" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const response = await acceptInvite({
        inviteToken: token,
        password: data.password,
      });

      if (response.data.status || response.data.success) {
        toast.success('Account setup complete! You can now log in.');
        navigate('/login');
      } else {
        toast.error(response.data.message || 'Failed to accept invite');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.description || err.response?.data?.message || 'An error occurred while setting your password.';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 relative overflow-hidden font-sans p-4">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>

      <div className="w-full max-w-md p-8 sm:p-10 space-y-8 glass rounded-2xl z-10 animate-slide-up">
        {/* Header */}
        <div className="text-center space-y-2">
          <img src="/icon.png" alt="Waya" className="w-12 h-12 mx-auto mb-6 object-contain drop-shadow-lg cursor-pointer" onClick={() => navigate('/login')} />
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Complete Your Setup</h2>
          <p className="text-slate-500 text-sm">Welcome to WayaBank! Set your password to get started.</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-widest px-1">
              <ShieldCheck size={14} />
              <span>Security</span>
            </div>
            
            <Input
              label="Create Password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
              error={errors.password?.message}
            />
            
            <Input
              label="Confirm Password"
              type="password"
              placeholder="••••••••"
              {...register('confirmPassword')}
              error={errors.confirmPassword?.message}
            />

            <div className="pt-4">
              <Button type="submit" className="w-full shadow-xl shadow-primary/20" isLoading={isLoading} size="lg">
                Complete Setup <Check size={18} className="ml-2" />
              </Button>
            </div>
          </div>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
            >
              Cancel and return to login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
